import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Mail, Instagram, Facebook, Youtube, Linkedin, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import NewsletterSignup from './NewsletterSignup';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Achievements', href: '/#achievements' },
    { name: 'Programs', href: '/#programs' },
    { name: 'Facilities', href: '/#facilities' },
    { name: 'Contact', href: '/#contact' },
  ];

  const policyLinks = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Refund Policy', href: '/refund' },
  ];

  const contactInfo = [
    { icon: Phone, text: '+91 96535 24055', href: 'tel:+919653524055', label: 'Call Us' },
    { icon: Mail, text: 'info@chaitanyashooting.com', href: 'mailto:info@chaitanyashooting.com', label: 'Email' },
    { icon: MapPin, text: 'Sector 81, Gurugram, Haryana', href: '#', label: 'Location' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/chaitanyashootingacademy/', label: 'Instagram' },
    { icon: Facebook, href: 'https://facebook.com/chaitanyashooting', label: 'Facebook' },
    { icon: Youtube, href: 'https://youtube.com/@chaitanyashooting', label: 'YouTube' },
    { icon: Linkedin, href: 'https://linkedin.com/company/chaitanya-shooting-academy', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-black border-t border-white/5">
      {/* Newsletter Section */}
      <NewsletterSignup />

      {/* Main Footer Content */}
      <div className="py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
                <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center text-white font-black text-xl group-hover:scale-110 transition-transform">
                  ⊙
                </div>
                <div>
                  <h3 className="font-black text-white text-lg leading-none">Chaitanya</h3>
                  <p className="text-[10px] text-red-600 font-black uppercase">Shooting Academy</p>
                </div>
              </Link>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Elite precision shooting academy training champions since 2024. Master the art of focus and excellence.
              </p>
              <div className="flex gap-3">
                {socialLinks.map((social, i) => (
                  <motion.a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-red-600 hover:border-red-600 transition-all"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-2">
                <Zap size={14} className="text-red-600" />
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    <a 
                      href={link.href}
                      className="text-zinc-400 hover:text-white transition-all flex items-center gap-2 group text-sm"
                    >
                      <span className="w-1 h-1 bg-red-600 rounded-full group-hover:translate-x-1 transition-transform" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Policies */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-2">
                <Zap size={14} className="text-red-600" />
                Legal
              </h4>
              <ul className="space-y-3">
                {policyLinks.map((link, i) => (
                  <li key={i}>
                    <a 
                      href={link.href}
                      className="text-zinc-400 hover:text-white transition-all flex items-center gap-2 group text-sm"
                    >
                      <span className="w-1 h-1 bg-red-600 rounded-full group-hover:translate-x-1 transition-transform" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-2">
                <Zap size={14} className="text-red-600" />
                Contact
              </h4>
              <ul className="space-y-4">
                {contactInfo.map((item, i) => (
                  <li key={i}>
                    <a 
                      href={item.href}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-5 h-5 rounded bg-red-600/20 flex items-center justify-center text-red-600 flex-shrink-0 mt-0.5 group-hover:bg-red-600 group-hover:text-white transition-all">
                        <item.icon size={14} />
                      </div>
                      <div>
                        <p className="text-[11px] text-zinc-600 uppercase font-black tracking-widest">{item.label}</p>
                        <p className="text-white text-sm hover:text-red-600 transition-colors">{item.text}</p>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 pt-8 md:pt-12">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-zinc-500 text-sm text-center md:text-left">
                &copy; {currentYear} Chaitanya Shooting Academy. All rights reserved.
              </p>
              <p className="text-zinc-500 text-sm flex items-center gap-2">
                Made with <span className="text-red-600">❤</span> for Excellence in Shooting
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
